import { Fragment, Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  FlagStripe,
  FriendStack,
  PhotoPlaceholder,
  VariantHeading,
} from '@/components/primitives';
import { CountryFilterSheet } from '@/components/CountryFilterSheet';
import { EventDetailSheet } from '@/components/EventDetailSheet';
import { getEvent } from '@/api/services';
import { EVENTS, track } from '@/lib/analytics';
import { inDateScope, type DateScope } from '@/lib/eventDateScope';
import { featureFlags } from '@/lib/flags';
import { haptics } from '@/lib/native';
import type {
  Country,
  CountryCode,
  Event,
  EventKind,
  FlagPalette,
  Match,
  Variant,
  Venue,
} from '@/types/domain';

// MapLibre (~80KB gz) is pulled only when the user opens the Map view. Lazy so
// the eagerly-imported Events tab doesn't drag it into the initial bundle; it
// resolves to the same chunk the Map tab already shares.
const EventsMapView = lazy(() =>
  import('@/components/EventsMapView').then((m) => ({ default: m.EventsMapView })),
);

interface EventsScreenProps {
  variant: Variant;
  events: Event[];
  eventRsvps: Set<string>;
  countriesByCode: Partial<Record<CountryCode, Country>>;
  follows: Partial<Record<CountryCode, boolean>>;
  matches: Match[];
  /** Flat venueId → Venue lookup from the store. Events reference venues
   * by id; we resolve names/hoods through this map. Missing entries
   * fall back to the event's inline `venueName` / `venueHood`. */
  venuesById: Record<string, Venue>;
  /** Fetches every venue so events referencing a venue by id can resolve
   * its name/hood. Idempotent — the store dedupes. */
  loadAllVenues: () => Promise<void>;
  /** When set on entry (e.g. deep-linked from a saved-event tap, SST-2),
   * open this event's detail sheet immediately. */
  focusEventId?: string;
  /** True once the venue store has finished loading — lets the Map view's
   * empty state tell "still loading coords" apart from "nothing to pin". */
  allVenuesLoaded?: boolean;
  onToggleRsvp: (eventId: string) => void;
}

// Only the dot color stays in code — it's presentational, not language. The
// human label/short come from the `events:kind.*` catalog at render time.
const KIND_DOT: Record<EventKind, string> = {
  watch:   '#d23',
  fanfest: '#0a7',
  village: '#0366d6',
  food:    '#c80',
  parade:  '#d8c',
  after:   '#5a4a3c',
};

// Filter chip ids in display order. "all" reuses events:filter.all; each kind
// reuses its events:kind.<kind>.short label.
const FILTER_IDS: (EventKind | 'all')[] = ['all', 'watch', 'fanfest', 'village', 'food', 'parade', 'after'];

const DEFAULT_PALETTE: FlagPalette = {
  primary: '#d23',
  secondary: '#0a0a0a',
  tertiary: '#5a544c',
};

const TZ = 'America/New_York';

// Decorative location-pin glyph. Rendered from a const (not a translation key)
// so it doesn't trip the i18n literal guard — it's presentational, not language.
const PIN_GLYPH = '📍';

// Decorative accent for the submit/reach-out CTA — const (not a translation
// key) so it doesn't trip the i18n literal guard; matches the ✦ used on Home.
const SPARK_GLYPH = '✦';

// YYYY-MM-DD in ET so cluster keys don't drift across DST or user tz.
function etYMD(d: Date): string {
  return d.toLocaleDateString('en-CA', { timeZone: TZ });
}

function formatTimeShort(iso: string): string {
  const d = new Date(iso);
  const t = d.toLocaleTimeString('en-US', {
    timeZone: TZ,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  // "8:00 PM" — strip the space before AM/PM for compactness.
  return t.replace(' ', ' ');
}

function formatWeekdayShort(d: Date): string {
  return d.toLocaleDateString('en-US', { timeZone: TZ, weekday: 'short' }).toUpperCase();
}

function formatMonthDay(d: Date): string {
  return d.toLocaleDateString('en-US', {
    timeZone: TZ,
    month: 'short',
    day: 'numeric',
  });
}

function formatWeekdayLong(d: Date): string {
  return d.toLocaleDateString('en-US', { timeZone: TZ, weekday: 'long' });
}

interface DayCluster {
  ymd: string;
  date: Date;
  label: string;
  weekday: string;
  monthDay: string;
  items: Event[];
}

/**
 * Group events by their ET calendar day and pick a headline label per day:
 *   - "Opening Day"        — date of the tournament's first kickoff
 *   - "MetLife Match Day"  — any match that day is at MetLife Stadium
 *   - "Today" / "Tomorrow" — relative to now
 *   - "Saturday" / etc.    — fallback weekday name
 *
 * The match data drives the headline so we don't need a separate editorial
 * column on the events row.
 */
function clusterEventsByDay(events: Event[], matches: Match[]): DayCluster[] {
  const buckets = new Map<string, Event[]>();
  for (const e of events) {
    const key = etYMD(new Date(e.startsAt));
    const list = buckets.get(key);
    if (list) list.push(e);
    else buckets.set(key, [e]);
  }

  // Per-date facts derived from the schedule. We only need to compute these
  // once per render even though many days share none of them.
  const matchesByYMD = new Map<string, Match[]>();
  for (const m of matches) {
    const k = etYMD(new Date(m.kickoffAt));
    const arr = matchesByYMD.get(k);
    if (arr) arr.push(m);
    else matchesByYMD.set(k, [m]);
  }
  const openerYMD = matches[0] ? etYMD(new Date(matches[0].kickoffAt)) : null;

  const todayYMD = etYMD(new Date());
  const tmwYMD = etYMD(new Date(Date.now() + 24 * 60 * 60 * 1000));

  return [...buckets.entries()]
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([ymd, items]) => {
      const date = new Date(items[0]!.startsAt);
      let label: string;
      if (ymd === openerYMD) {
        label = 'Opening Day';
      } else if ((matchesByYMD.get(ymd) ?? []).some((m) => m.stadium.includes('MetLife'))) {
        label = 'MetLife Match Day';
      } else if (ymd === todayYMD) {
        label = 'Today';
      } else if (ymd === tmwYMD) {
        label = 'Tomorrow';
      } else {
        label = formatWeekdayLong(date);
      }
      return {
        ymd,
        date,
        label,
        weekday: formatWeekdayShort(date),
        monthDay: formatMonthDay(date),
        items,
      };
    });
}

export function EventsScreen({
  events,
  eventRsvps,
  countriesByCode,
  follows,
  matches,
  venuesById,
  loadAllVenues,
  focusEventId,
  allVenuesLoaded = false,
  onToggleRsvp,
}: EventsScreenProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<EventKind | 'all'>('all');
  const [countryFilter, setCountryFilter] = useState<Set<CountryCode>>(() => new Set());
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [dateScope, setDateScope] = useState<DateScope>('all');

  const changeView = useCallback((next: 'list' | 'map') => {
    haptics.selection();
    setViewMode(next);
    track(EVENTS.eventsViewToggled, { view: next });
  }, []);

  // Size the map to exactly fill the space between the header/filters above it
  // and the fixed TabBar below — measured, not guessed, so it's right across
  // locales and phone sizes (a hardcoded subtraction pushed the bottom edge,
  // and the spots pill, behind the tab bar). 64px = TabBar height (App's main
  // reserves the same).
  const mapWrapRef = useRef<HTMLDivElement>(null);
  const [mapHeight, setMapHeight] = useState('60vh');
  useEffect(() => {
    if (viewMode !== 'map') return;
    const measure = () => {
      const el = mapWrapRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      setMapHeight(`calc(100dvh - ${Math.round(top)}px - 64px - env(safe-area-inset-bottom, 0px))`);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [viewMode]);

  // Events reference venues by id; pull every venue on mount so those
  // names resolve instead of rendering a location-less card.
  useEffect(() => {
    void loadAllVenues();
  }, [loadAllVenues]);

  // Only offer countries as filter options when they actually have at
  // least one event (own country_code or linked match's home/away).
  // Drives both the Country sheet contents and whether the trigger
  // button is even rendered.
  const countryOptions = useMemo(() => {
    const codes = new Set<CountryCode>();
    for (const ev of events) {
      if (ev.countryCode) codes.add(ev.countryCode);
      if (ev.matchId) {
        const m = matches.find((mm) => mm.id === ev.matchId);
        if (m?.home) codes.add(m.home);
        if (m?.away) codes.add(m.away);
      }
    }
    return Array.from(codes)
      .map((code) => countriesByCode[code])
      .filter((c): c is Country => Boolean(c));
  }, [events, matches, countriesByCode]);

  const filtered = useMemo(() => {
    const now = new Date();
    let list = filter === 'all' ? events : events.filter((e) => e.kind === filter);
    if (dateScope !== 'all') list = list.filter((e) => inDateScope(e, dateScope, now));
    if (countryFilter.size > 0) {
      list = list.filter((e) => {
        if (e.countryCode && countryFilter.has(e.countryCode)) return true;
        if (!e.matchId) return false;
        const m = matches.find((mm) => mm.id === e.matchId);
        if (m?.home && countryFilter.has(m.home)) return true;
        if (m?.away && countryFilter.has(m.away)) return true;
        return false;
      });
    }
    return list;
  }, [events, matches, filter, countryFilter, dateScope]);

  const days = useMemo(() => clusterEventsByDay(filtered, matches), [filtered, matches]);

  const [showSubmitSheet, setShowSubmitSheet] = useState(false);
  // Seed from a deep-link (saved-event tap, SST-2) so the sheet is already
  // up on entry; tapping a card later sets it the normal way.
  const [openEventId, setOpenEventId] = useState<string | null>(focusEventId ?? null);
  // A shared link (`/e/<id>`) can deep-link to an event that's aged out of the
  // default feed window, so it isn't in `events`. We fetch it by id on miss and
  // keep it here so the detail sheet still opens.
  const [fetchedEvent, setFetchedEvent] = useState<Event | null>(null);
  const openEvent = openEventId
    ? events.find((e) => e.id === openEventId)
      ?? (fetchedEvent?.id === openEventId ? fetchedEvent : null)
    : null;

  useEffect(() => {
    if (!openEventId) return;
    if (events.some((e) => e.id === openEventId)) return;
    if (fetchedEvent?.id === openEventId) return;
    let cancelled = false;
    getEvent(openEventId)
      .then((ev) => {
        if (!cancelled) setFetchedEvent(ev);
      })
      .catch(() => {
        // 404 / network — the link is dead or offline; leave the sheet closed.
      });
    return () => {
      cancelled = true;
    };
  }, [openEventId, events, fetchedEvent]);

  // If we deep-link to a different event while already mounted (e.g. a second
  // saved-event tap without leaving the Events tab), re-open to the new one.
  useEffect(() => {
    if (focusEventId) {
      setOpenEventId(focusEventId);
      track(EVENTS.eventDetailOpened, { eventId: focusEventId, source: 'deeplink' });
    }
  }, [focusEventId]);

  // Back-gesture dismiss for the event detail sheet. When the sheet opens we
  // push a sentinel history entry so browser-back / iOS-swipe-back / Android-
  // hardware-back closes the sheet instead of leaving the screen.
  //
  // The cleanup deliberately does NOT touch history — only the X button
  // consumes the sentinel (via closeSheet below). Popping in cleanup breaks
  // the saved-event deep-link: that path mounts with openEventId already set,
  // so StrictMode's mount→unmount→remount fires a spurious back() whose async
  // popstate slams the freshly-opened sheet shut ("nothing opens"). The push
  // is guarded so the StrictMode remount doesn't stack a second sentinel.
  const openEventIdRef = useRef(openEventId);
  openEventIdRef.current = openEventId;
  useEffect(() => {
    if (openEventId === null) return;
    if (window.history.state?.eventSheet !== openEventId) {
      window.history.pushState({ eventSheet: openEventId }, '');
    }
    const onPop = () => setOpenEventId(null);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [openEventId]);

  // X-button close. A back gesture already pops the sentinel (its popstate
  // clears openEventId); the X button has to pop it itself so a later Back
  // press doesn't resurface the sheet. The back() fires popstate → null.
  const closeSheet = useCallback(() => {
    if (window.history.state?.eventSheet === openEventIdRef.current) {
      window.history.back();
    } else {
      setOpenEventId(null);
    }
  }, []);

  return (
    <>
      <Header viewMode={viewMode} onChangeView={changeView} t={t} />
      <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 16 }}>
        <CountryFilterTrigger
          countryCount={countryFilter.size}
          dateActive={dateScope !== 'all'}
          onOpen={() => {
            haptics.light();
            setShowFilterSheet(true);
          }}
          t={t}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <FilterChips filter={filter} setFilter={setFilter} t={t} />
        </div>
      </div>

      {viewMode === 'map' ? (
        <div ref={mapWrapRef}>
          <Suspense fallback={<MapFallback t={t} />}>
            <EventsMapView
              events={filtered}
              venuesById={venuesById}
              countriesByCode={countriesByCode}
              venuesLoading={!allVenuesLoaded}
              mapHeight={mapHeight}
              onOpenEvent={(id) => {
                track(EVENTS.eventDetailOpened, { eventId: id, source: 'map' });
                setOpenEventId(id);
              }}
            />
          </Suspense>
        </div>
      ) : (
        <>
      {/* Top reach-out prompt (SST-25): a stranger should be able to flag an
          event / watch party right away, instead of only finding the submit
          card buried after the 10th event. Opens the same sheet (SST-40 copy
          + Instagram handle) as the inline cards. */}
      <div style={{ padding: '10px 20px 0' }}>
        <ReachOutBanner
          onOpen={() => {
            track(EVENTS.submitCtaTapped, { surface: 'reachout_banner' });
            setShowSubmitSheet(true);
          }}
          t={t}
        />
      </div>

      {days.length === 0 ? (
        <div
          style={{
            padding: '40px 24px',
            textAlign: 'center',
            color: '#8a7f72',
            fontSize: 14,
          }}
        >
          {events.length === 0
            ? t('events:noEventsYet')
            : t('events:nothingInKind')}
        </div>
      ) : (() => {
        // Insert a "submit something" prompt after every 10th event in
        // the global ordering — gives passers-by a way in without
        // doubling up. Rotates through a small copy pool so it doesn't
        // read like an ad.
        let globalIndex = 0;
        return days.map((day) => (
          <div key={day.ymd}>
            <DayHeader day={day} t={t} />
            <div style={{ padding: '0 20px' }}>
              {day.items.map((ev) => {
                globalIndex += 1;
                const country = ev.countryCode ? countriesByCode[ev.countryCode] : undefined;
                const venue = ev.venueId ? venuesById[ev.venueId] : undefined;
                const isGoing = eventRsvps.has(ev.id);
                const showCta = globalIndex % 10 === 0;
                const ctaVariantIndex = globalIndex / 10 - 1;
                return (
                  <Fragment key={ev.id}>
                    <EventCard
                      ev={ev}
                      country={country}
                      venue={venue}
                      isGoing={isGoing}
                      t={t}
                      onToggle={() => {
                        // Haptic-only commit feedback — the going sound was
                        // dropped per UX feedback (vibration is enough).
                        if (!isGoing) {
                          haptics.success();
                        } else {
                          haptics.light();
                        }
                        onToggleRsvp(ev.id);
                      }}
                      onOpen={() => {
                        track(EVENTS.eventDetailOpened, {
                          eventId: ev.id,
                          kind: ev.kind,
                          source: 'list',
                        });
                        setOpenEventId(ev.id);
                      }}
                    />
                    {showCta && (
                      <InlineSubmitCard
                        variantIndex={ctaVariantIndex}
                        onOpen={() => {
                          track(EVENTS.submitCtaTapped, { surface: 'inline_card' });
                          setShowSubmitSheet(true);
                        }}
                        t={t}
                      />
                    )}
                  </Fragment>
                );
              })}
            </div>
          </div>
        ));
      })()}
      <div style={{ height: 100 }} />
        </>
      )}

      {showSubmitSheet && <SubmitSheet onClose={() => setShowSubmitSheet(false)} t={t} />}

      {showFilterSheet && (
        <CountryFilterSheet
          countries={countryOptions}
          follows={follows}
          selected={countryFilter}
          setSelected={setCountryFilter}
          dateScope={dateScope}
          setDateScope={setDateScope}
          onClose={() => setShowFilterSheet(false)}
        />
      )}

      {openEvent && (() => {
        // Resolve the linked match (if any) and both team countries so
        // the sheet can show "🇧🇷 Brazil vs Argentina 🇦🇷" for watch
        // parties instead of a single ambiguous country chip.
        const match = openEvent.matchId
          ? matches.find((m) => m.id === openEvent.matchId)
          : undefined;
        const matchHome = match?.home ? countriesByCode[match.home] : undefined;
        const matchAway = match?.away ? countriesByCode[match.away] : undefined;
        return (
          <EventDetailSheet
            event={openEvent}
            country={openEvent.countryCode ? countriesByCode[openEvent.countryCode] : undefined}
            venue={openEvent.venueId ? venuesById[openEvent.venueId] : undefined}
            matchHome={matchHome}
            matchAway={matchAway}
            isGoing={eventRsvps.has(openEvent.id)}
            onToggle={() => {
              // Haptic-only — see the list card above. The going sound was
              // dropped per UX feedback (vibration alone is enough).
              const wasGoing = eventRsvps.has(openEvent.id);
              if (!wasGoing) {
                haptics.success();
              } else {
                haptics.light();
              }
              onToggleRsvp(openEvent.id);
            }}
            onClose={closeSheet}
          />
        );
      })()}
    </>
  );
}

// Compact List | Map segmented switch, sized to sit inline at the top-right of
// the events header (frees the vertical room a full-width row would eat).
function ViewToggle({
  viewMode,
  onChange,
  t,
}: {
  viewMode: 'list' | 'map';
  onChange: (next: 'list' | 'map') => void;
  t: TFunction;
}) {
  const tabs: { id: 'list' | 'map'; key: string }[] = [
    { id: 'list', key: 'events:viewMode.list' },
    { id: 'map', key: 'events:viewMode.map' },
  ];
  return (
    <div
      role="tablist"
      aria-label={t('events:viewMode.ariaLabel')}
      style={{
        flexShrink: 0,
        background: '#ece8e2',
        borderRadius: 999,
        padding: 3,
        display: 'flex',
        gap: 2,
      }}
    >
      {tabs.map((tab) => {
        const active = viewMode === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.id)}
            style={{
              background: active ? '#fff' : 'transparent',
              color: active ? '#0a0a0a' : '#5a544c',
              padding: '6px 16px',
              borderRadius: 999,
              border: 'none',
              fontFamily: '-apple-system, system-ui',
              fontSize: 13,
              fontWeight: active ? 700 : 600,
              boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'background .18s ease, color .18s ease, box-shadow .18s ease',
              cursor: 'pointer',
            }}
          >
            {t(tab.key)}
          </button>
        );
      })}
    </div>
  );
}

// Suspense fallback while the lazy map chunk loads.
function MapFallback({ t }: { t: TFunction }) {
  return (
    <div style={{ padding: '60px 24px', textAlign: 'center', color: '#8a7f72', fontSize: 14 }}>
      {t('events:map.loading')}
    </div>
  );
}

function Header({
  viewMode,
  onChangeView,
  t,
}: {
  viewMode: 'list' | 'map';
  onChangeView: (next: 'list' | 'map') => void;
  t: TFunction;
}) {
  return (
    <div
      style={{
        padding: 'max(24px, env(safe-area-inset-top)) 20px 18px',
        background: 'linear-gradient(160deg, #d2331a18, #0a7f0a10, #0a0a0a14)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Top row: the GOING ON badge (left) and the List/Map switch (right) —
          the toggle moved up here so it costs no extra vertical row. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: '#0a0a0a',
            color: '#fff',
            padding: '5px 10px',
            borderRadius: 999,
            fontFamily: '-apple-system, system-ui',
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: 1,
            textTransform: 'uppercase',
          }}
        >
          <span
            style={{ width: 6, height: 6, borderRadius: 3, background: '#d23' }}
          />
          {t('events:goingOn')}
        </span>
        <ViewToggle viewMode={viewMode} onChange={onChangeView} t={t} />
      </div>
      <div
        style={{
          fontFamily: '-apple-system, system-ui',
          fontSize: 38,
          fontWeight: 900,
          color: '#0a0a0a',
          letterSpacing: -1,
          lineHeight: 1,
        }}
      >
        {t('events:title')}
      </div>
      <div
        style={{
          fontFamily: '-apple-system, system-ui',
          fontSize: 13,
          color: '#3a342c',
          marginTop: 8,
          fontWeight: 500,
        }}
      >
        {t('events:subtitle')}
      </div>
    </div>
  );
}

// Edge fade widths — the gradient that signals "more chips this way".
const CHIP_FADE_W = 32;
// Page background the fade resolves to (App's <main>).
const PAGE_BG = '#f7f5f1';

function CountryFilterTrigger({
  countryCount,
  dateActive,
  onOpen,
  t,
}: {
  countryCount: number;
  dateActive: boolean;
  onOpen: () => void;
  t: TFunction;
}) {
  // Light up for either filter dimension — teams or a non-default date scope.
  const active = countryCount > 0 || dateActive;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={active ? t('events:filterByTeamSelected', { count: countryCount }) : t('events:filterByTeam')}
      style={{
        position: 'relative',
        flexShrink: 0,
        width: 34,
        height: 34,
        marginRight: 6,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: active ? '#0a0a0a' : '#fff',
        color: active ? '#fff' : '#0a0a0a',
        border: active ? 'none' : '1px solid #ece8e2',
        borderRadius: 999,
        cursor: 'pointer',
      }}
    >
      <svg
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
      </svg>
      {active && (
        // Team count when teams are picked; a plain dot when only the date
        // scope is active (so "0" never shows).
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: -4,
            right: -4,
            minWidth: countryCount > 0 ? 16 : 10,
            height: countryCount > 0 ? 16 : 10,
            padding: countryCount > 0 ? '0 4px' : 0,
            borderRadius: 999,
            background: '#d23',
            color: '#fff',
            fontFamily: '-apple-system, system-ui',
            fontSize: 10,
            fontWeight: 800,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {countryCount > 0 ? countryCount : ''}
        </span>
      )}
    </button>
  );
}

// The display label for a filter chip: "all" → events:filter.all; each kind
// reuses its short label from events:kind.<kind>.short.
function filterLabel(id: EventKind | 'all', t: TFunction): string {
  return id === 'all' ? t('events:filter.all') : t(`events:kind.${id}.short`);
}

function FilterChips({
  filter,
  setFilter,
  t,
}: {
  filter: EventKind | 'all';
  setFilter: (id: EventKind | 'all') => void;
  t: TFunction;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Which edges have off-screen content — drives the fade overlays.
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  const updateEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateEdges();
    window.addEventListener('resize', updateEdges);
    return () => window.removeEventListener('resize', updateEdges);
  }, [updateEdges]);

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={scrollRef}
        className="scroll-hide"
        onScroll={updateEdges}
        style={{
          display: 'flex',
          gap: 6,
          padding: '14px 20px 12px 6px',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
        }}
      >
        {FILTER_IDS.map((id) => {
          const active = filter === id;
          return (
            <button
              key={id}
              type="button"
              // Tapping the active chip clears it back to "All".
              onClick={() => {
                haptics.selection();
                setFilter(id === filter ? 'all' : id);
              }}
              style={{
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: active ? '#0a0a0a' : '#fff',
                color: active ? '#fff' : '#0a0a0a',
                border: active ? 'none' : '1px solid #ece8e2',
                padding: '7px 12px',
                borderRadius: 999,
                fontFamily: '-apple-system, system-ui',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              {/* The dot matches the kind's map-pin color, so the kind filter
                  doubles as the map legend. "All" has no single color. */}
              {id !== 'all' && (
                <span
                  aria-hidden
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: KIND_DOT[id],
                    flexShrink: 0,
                    // Keep the dark 'after' dot legible on the active black chip.
                    boxShadow: active ? '0 0 0 1px rgba(255,255,255,.5)' : undefined,
                  }}
                />
              )}
              {filterLabel(id, t)}
            </button>
          );
        })}
      </div>
      {!atStart && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: CHIP_FADE_W,
            pointerEvents: 'none',
            background: `linear-gradient(90deg, ${PAGE_BG}, transparent)`,
          }}
        />
      )}
      {!atEnd && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: CHIP_FADE_W,
            pointerEvents: 'none',
            background: `linear-gradient(270deg, ${PAGE_BG}, transparent)`,
          }}
        />
      )}
    </div>
  );
}

function DayHeader({ day, t }: { day: DayCluster; t: TFunction }) {
  return (
    <div style={{ padding: '14px 20px 6px', marginTop: 8 }}>
      <VariantHeading variant="sporty">{day.label}</VariantHeading>
      <div
        style={{
          fontSize: 11,
          color: '#9a9590',
          fontWeight: 600,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          marginTop: -8,
          marginBottom: 4,
        }}
      >
        {day.weekday} · {day.monthDay} · {t('events:eventCount', { count: day.items.length })}
      </div>
    </div>
  );
}

function EventCard({
  ev,
  country,
  venue,
  isGoing,
  onToggle,
  onOpen,
  t,
}: {
  ev: Event;
  country: Country | undefined;
  venue: Venue | undefined;
  isGoing: boolean;
  onToggle: () => void;
  onOpen: () => void;
  t: TFunction;
}) {
  const palette = country?.colors ?? DEFAULT_PALETTE;
  const kindDot = KIND_DOT[ev.kind];
  const kindShort = t(`events:kind.${ev.kind}.short`);
  const venueDisplay = venue?.name ?? ev.venueName ?? '';
  const hoodDisplay = venue?.hood ?? ev.venueHood ?? '';
  const location = [venueDisplay, hoodDisplay].filter(Boolean).join(' · ');

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      style={{
        background: '#fff',
        borderRadius: 16,
        marginBottom: 12,
        overflow: 'hidden',
        border: '1px solid #f0ede8',
        cursor: 'pointer',
      }}
    >
      <div style={{ position: 'relative' }}>
        {/* When an event has an uploaded image we render the actual flyer
            with its natural aspect (capped to keep portrait IG screenshots
            from going absurdly tall). Imageless events keep the generated
            placeholder so the card still has a visual header. */}
        {ev.imageUrl ? (
          <img
            src={ev.imageUrl}
            alt=""
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: 420,
              objectFit: 'contain',
              display: 'block',
              background: '#0a0a0a',
            }}
          />
        ) : (
          <PhotoPlaceholder height={120} palette={palette} />
        )}
        <div
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            background: 'rgba(255,255,255,0.95)',
            padding: '4px 8px',
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            color: '#0a0a0a',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
          }}
        >
          <span style={{ width: 5, height: 5, borderRadius: 3, background: kindDot }} />
          {kindShort}
        </div>
        <div
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            background: '#0a0a0a',
            color: '#fff',
            padding: '4px 9px',
            borderRadius: 6,
            fontFamily: '-apple-system, system-ui',
            fontSize: 11,
            fontWeight: 800,
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: 0.4,
          }}
        >
          {formatTimeShort(ev.startsAt)}
        </div>
        {(ev.isHot || ev.isFree) && (
          <div
            style={{
              position: 'absolute',
              bottom: 10,
              left: 10,
              display: 'flex',
              gap: 6,
            }}
          >
            {ev.isHot && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  background: '#d23',
                  color: '#fff',
                  padding: '4px 8px',
                  borderRadius: 999,
                  fontSize: 9.5,
                  fontWeight: 800,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                }}
              >
                <span
                  style={{ width: 5, height: 5, borderRadius: 3, background: '#fff' }}
                />
                {t('events:hot')}
              </span>
            )}
            {ev.isFree && (
              <span
                style={{
                  background: '#0a7',
                  color: '#fff',
                  padding: '4px 8px',
                  borderRadius: 999,
                  fontSize: 9.5,
                  fontWeight: 800,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                }}
              >
                {t('common:free')}
              </span>
            )}
          </div>
        )}
        {country && (
          <div
            style={{
              position: 'absolute',
              bottom: 10,
              right: 10,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              background: 'rgba(255,255,255,0.95)',
              padding: '3px 8px 3px 4px',
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 800,
              color: '#0a0a0a',
              letterSpacing: 0.6,
            }}
          >
            <FlagStripe code={country.code} colors={country.colors} w={14} h={10} radius={1.5} />
            {country.code}
          </div>
        )}
      </div>
      <div style={{ padding: '12px 14px' }}>
        <div
          style={{
            fontFamily: '-apple-system, system-ui',
            fontSize: 16,
            fontWeight: 800,
            color: '#0a0a0a',
            lineHeight: 1.25,
            marginBottom: location ? 4 : 12,
          }}
        >
          {ev.title}
        </div>
        {location ? (
          <div style={{ fontSize: 12, color: '#5a544c', marginBottom: 12 }}>{PIN_GLYPH} {location}</div>
        ) : null}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            onClick={(e) => {
              // The whole card is tappable to open the detail sheet — don't
              // let the RSVP toggle also open it.
              e.stopPropagation();
              onToggle();
            }}
            style={{
              border: 'none',
              // Going state is a consistent green across all events (SST-1) —
              // previously took each event's country color, so it varied.
              background: isGoing ? '#0a7f0a' : '#0a0a0a',
              color: '#fff',
              padding: '9px 18px',
              borderRadius: 999,
              fontFamily: '-apple-system, system-ui',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              boxShadow: isGoing ? '0 4px 14px #0a7f0a66' : 'none',
              transition: 'all .2s',
            }}
          >
            {isGoing ? t('common:goingCheck') : t('common:imGoing')}
          </button>
          <span style={{ flex: 1 }} />
          {featureFlags.goingCount() && (
            <>
              {ev.goingCount > 50 && (
                <FriendStack count={Math.min(4, Math.floor(ev.goingCount / 50))} size={22} />
              )}
              <span style={{ fontSize: 11, color: '#5a544c', fontWeight: 600 }}>
                {t('common:plusValue', { value: ev.goingCount.toLocaleString() })}
              </span>
            </>
          )}
          {/* "Details ›" affordance, bottom-right — signals the card opens the
              detail sheet (SST-33). */}
          <span style={{ fontSize: 12, fontWeight: 700, color: '#8a7f72', whiteSpace: 'nowrap' }}>
            {t('events:details')}
          </span>
        </div>
      </div>
    </div>
  );
}

const INLINE_CTA_VARIANTS: { headlineKey: string; ctaKey: string }[] = [
  { headlineKey: 'events:cta.missingHeadline', ctaKey: 'events:cta.missingCta' },
  { headlineKey: 'events:cta.missedHeadline',  ctaKey: 'events:cta.missedCta' },
  { headlineKey: 'events:cta.flyerHeadline',   ctaKey: 'events:cta.flyerCta' },
  { headlineKey: 'events:cta.reppingHeadline', ctaKey: 'events:cta.reppingCta' },
];

function InlineSubmitCard({
  variantIndex,
  onOpen,
  t,
}: {
  variantIndex: number;
  onOpen: () => void;
  t: TFunction;
}) {
  const v = INLINE_CTA_VARIANTS[variantIndex % INLINE_CTA_VARIANTS.length]!;
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        width: '100%',
        marginBottom: 12,
        padding: '14px 16px',
        background: 'transparent',
        border: '1.5px dashed #c8c2b6',
        borderRadius: 12,
        textAlign: 'left',
        cursor: 'pointer',
        fontFamily: '-apple-system, system-ui',
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 500, color: '#5a544c' }}>{t(v.headlineKey)}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: '#0a0a0a', flexShrink: 0 }}>{t(v.ctaKey)}</span>
    </button>
  );
}

/**
 * Prominent reach-out prompt pinned to the top of the Events page (SST-25):
 * a soft filled card (not the dashed inline style) inviting anyone who knows
 * something happening around a World Cup match to message us so we add it.
 * Opens the same submit sheet as the inline cards.
 */
function ReachOutBanner({ onOpen, t }: { onOpen: () => void; t: TFunction }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '14px 16px',
        background: '#faf7f2',
        border: '1px solid #ece8e2',
        borderRadius: 14,
        textAlign: 'left',
        cursor: 'pointer',
        fontFamily: '-apple-system, system-ui',
      }}
    >
      <span aria-hidden="true" style={{ color: '#d23', fontSize: 15, flexShrink: 0 }}>
        {SPARK_GLYPH}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 800, color: '#0a0a0a', lineHeight: 1.25 }}>
          {t('events:reachOut.headline')}
        </span>
        <span style={{ display: 'block', fontSize: 12, color: '#5a544c', marginTop: 2, lineHeight: 1.35 }}>
          {t('events:reachOut.sub')}
        </span>
      </span>
      <span style={{ fontSize: 13, fontWeight: 700, color: '#d23', flexShrink: 0, whiteSpace: 'nowrap' }}>
        {t('events:reachOut.cta')}
      </span>
    </button>
  );
}

function SubmitSheet({ onClose, t }: { onClose: () => void; t: TFunction }) {
  return (
    <div
      role="presentation"
      // Only close when the click is on the backdrop itself, not on the
      // sheet contents — saves us from putting stopPropagation on a
      // non-interactive child div (which the a11y linter dislikes).
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        background: 'rgba(20, 16, 12, 0.55)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('events:submit.dialogLabel')}
        style={{
          width: '100%',
          maxWidth: 560,
          background: '#fff',
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          padding: '20px 22px calc(env(safe-area-inset-bottom, 0px) + 24px)',
          fontFamily: '-apple-system, system-ui',
        }}
      >
        <div
          style={{
            width: 36,
            height: 4,
            borderRadius: 2,
            background: '#e0dad0',
            margin: '0 auto 16px',
          }}
        />
        <div style={{ fontSize: 22, fontWeight: 800, color: '#0a0a0a', marginBottom: 8 }}>
          {t('events:submit.heading')}
        </div>
        <div style={{ fontSize: 14, color: '#5a544c', lineHeight: 1.5, marginBottom: 18 }}>
          {t('events:submit.body')}
        </div>
        <a
          href="https://instagram.com/nynjworldcup"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            textAlign: 'center',
            background: '#0a0a0a',
            color: '#fff',
            padding: '12px 16px',
            borderRadius: 12,
            fontSize: 14,
            fontWeight: 700,
            textDecoration: 'none',
            marginBottom: 8,
          }}
        >
          {t('events:submit.instagram')}
        </a>
        <button
          type="button"
          onClick={onClose}
          style={{
            display: 'block',
            width: '100%',
            background: 'transparent',
            border: 'none',
            padding: '12px 16px',
            color: '#5a544c',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {t('events:submit.close')}
        </button>
      </div>
    </div>
  );
}
